import { lowerCase } from "lodash";
import { AppConstants } from "../constants/AppConstants";

export const formatDate = (date: any) => {
  return new Date(date).toISOString().split("T")[0];
};

export const formatPhoneNumber = (value: any) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, "");
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4) return `${phoneNumber}`;

  if (phoneNumberLength < 7) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  }

  return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

export const getCountryCodeViaLocale = (locale: string | undefined) => {
  if (!locale) return;
  const parts = locale.split("-");
  if (parts.length < 2) return;
  let country = lowerCase(parts[1]);
  if (country.length) {
    localStorage.setItem(AppConstants.LOCAL_STORAGE_COUNTRY_KEY, country);
  }
};

// RTL Support Functions
export const isRTLLanguage = (language: string | undefined): boolean => {
  if (!language) return false;
  const langCode = language.split("-")[0].toLowerCase();
  return AppConstants.RTL_LANGUAGES.includes(langCode);
};

export const getTextDirection = (language: string | undefined): string => {
  return isRTLLanguage(language) ? AppConstants.DIRECTION_RTL : AppConstants.DIRECTION_LTR;
};

export const setDocumentDirection = (language: string | undefined): void => {
  const direction = getTextDirection(language);
  document.documentElement.setAttribute("dir", direction);
  document.documentElement.setAttribute("lang", language || "en");
  localStorage.setItem(AppConstants.LOCAL_STORAGE_DIRECTION_KEY, direction);
};

export const isArabicLanguage = (language: string | undefined): boolean => {
  return language?.startsWith("ar") || false;
};

export const formatNumberForLocale = (number: number | string, locale: string): string => {
  if (isArabicLanguage(locale)) {
    return new Intl.NumberFormat(AppConstants.ARABIC_NUMBER_FORMAT).format(Number(number));
  }
  return new Intl.NumberFormat(locale).format(Number(number));
};

export const formatDateForLocale = (date: Date | string, locale: string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isArabicLanguage(locale)) {
    return dateObj.toLocaleDateString('ar-SA-islamic', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      calendar: 'islamic-umalqura'
    });
  }
  return dateObj.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const getFlexDirection = (isRTL: boolean): string => {
  return isRTL ? "flex-row-reverse" : "flex-row";
};

export const getTextAlign = (isRTL: boolean): string => {
  return isRTL ? "text-right" : "text-left";
};

export const getFloatDirection = (isRTL: boolean): string => {
  return isRTL ? "float-right" : "float-left";
};

export const getMarginDirection = (isRTL: boolean, side: 'left' | 'right'): string => {
  if (isRTL) {
    return side === 'left' ? 'mr' : 'ml';
  }
  return side === 'left' ? 'ml' : 'mr';
};

export const getPaddingDirection = (isRTL: boolean, side: 'left' | 'right'): string => {
  if (isRTL) {
    return side === 'left' ? 'pr' : 'pl';
  }
  return side === 'left' ? 'pl' : 'pr';
};

export const getRTLSafeClass = (baseClass: string, isRTL: boolean): string => {
  if (!isRTL) return baseClass;
  
  // Handle common directional classes
  const rtlMappings: { [key: string]: string } = {
    'ml-': 'mr-',
    'mr-': 'ml-',
    'pl-': 'pr-',
    'pr-': 'pl-',
    'left-': 'right-',
    'right-': 'left-',
    'text-left': 'text-right',
    'text-right': 'text-left',
    'float-left': 'float-right',
    'float-right': 'float-left',
    'border-l-': 'border-r-',
    'border-r-': 'border-l-',
    'rounded-l-': 'rounded-r-',
    'rounded-r-': 'rounded-l-'
  };

  let processedClass = baseClass;
  Object.entries(rtlMappings).forEach(([ltr, rtl]) => {
    if (processedClass.includes(ltr)) {
      processedClass = processedClass.replace(new RegExp(ltr, 'g'), rtl);
    } else if (processedClass.includes(rtl)) {
      processedClass = processedClass.replace(new RegExp(rtl, 'g'), ltr);
    }
  });

  return processedClass;
};
